/**
 * /hospitality/night-audit — the hotel/resort night-audit close.
 *
 * Mirrors the property's nightly revenue close: a Daily Revenue Journal
 * (the balanced GL entry built from every outlet's POS/PMS statements),
 * an income audit per revenue center (reported vs settled → over/short),
 * and an exceptions list the night auditor must clear before posting.
 *
 * All money fields arrive as decimal strings; `money()` formats them.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import {
  PageHeader,
  StatCard,
  Panel,
  StatusPill,
  SeverityIcon,
  money,
} from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/night-audit")({
  component: NightAuditPage,
});

// ── Types — matches GET /api/hospitality/night-audit contract ──────────────
interface DrjLine {
  account: string;
  label: string;
  debit: string;
  credit: string;
}
interface Drj {
  lines: DrjLine[];
  total_debit: string;
  total_credit: string;
  variance: string;
  balanced: boolean;
}
interface AuditRow {
  revenue_center: string;
  department: string;
  reported: string;
  settled: string;
  over_short: string;
  open_reconciling: string;
  status: "cleared" | "open" | "exception";
}
interface NightAuditException {
  severity: "high" | "medium" | "info";
  type: string;
  revenue_center: string | null;
  title: string;
  detail: string;
}
interface ParkedRow {
  revenue_center: string;
  unmapped_txn_types: string[];
}
interface NightAuditResult {
  property: string;
  business_date: string | null;
  statements_count: number;
  posted_count: number | null;
  drj: Drj | null;
  audits: AuditRow[];
  net_over_short: string;
  exceptions: NightAuditException[];
  parked: ParkedRow[];
  message?: string;
}

// ── Formatters ─────────────────────────────────────────────────────────────
function moneyOrBlank(v: string): string {
  // DRJ debit/credit columns: render blank for the "0.00" side.
  if (v == null || Number(v) === 0) return "";
  return money(v);
}

function NightAuditPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-night-audit"],
    queryFn: () => api<NightAuditResult>("/api/hospitality/night-audit"),
  });

  const data = q.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Night Audit"
        subtitle={
          data
            ? `${data.property}${data.business_date ? ` · ${data.business_date}` : ""}`
            : "Daily revenue journal + over/short income audit"
        }
        right={
          data?.drj ? (
            <StatusPill tone={data.drj.balanced ? "success" : "exception"}>
              {data.drj.balanced ? "Balanced" : `Out of balance ${money(data.drj.variance)}`}
            </StatusPill>
          ) : undefined
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
            <br />
            <span className="text-xs">
              Night-audit results populate once outlet statements are imported for a business date.
            </span>
          </div>
        )}

        {!q.isLoading && data && !data.message && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard
                label="Daily Revenue Journal"
                value={data.drj ? money(data.drj.total_credit, 0) : "—"}
                tone={data.drj && !data.drj.balanced ? "danger" : "default"}
              />
              <StatCard
                label="Net over/short"
                value={money(data.net_over_short)}
                tone={Number(data.net_over_short) !== 0 ? "warning" : "success"}
              />
              <StatCard
                label="Outlets audited"
                value={String(data.posted_count ?? data.audits.length)}
              />
              <StatCard
                label="Held for mapping"
                value={String(data.parked.length)}
                tone={data.parked.length > 0 ? "warning" : "default"}
              />
            </div>

            {/* Daily Revenue Journal */}
            {data.drj && (
              <Panel
                label="Daily Revenue Journal"
                right={
                  <StatusPill tone={data.drj.balanced ? "success" : "exception"}>
                    {data.drj.balanced ? "Balanced" : `Variance ${money(data.drj.variance)}`}
                  </StatusPill>
                }
              >
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="text-left py-1.5 pr-3">Account</th>
                      <th className="text-left py-1.5 px-3">Label</th>
                      <th className="text-right py-1.5 px-3">Debit</th>
                      <th className="text-right py-1.5 pl-3">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.drj.lines.map((l, i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 pr-3 font-mono text-xs">{l.account}</td>
                        <td className="py-1.5 px-3 text-slate-700 dark:text-slate-300">{l.label}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs">
                          {moneyOrBlank(l.debit)}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums text-xs">
                          {moneyOrBlank(l.credit)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-slate-300 dark:border-slate-600 font-medium">
                      <td className="py-2 pr-3" colSpan={2}>
                        TOTALS
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{money(data.drj.total_debit)}</td>
                      <td className="py-2 pl-3 text-right tabular-nums">{money(data.drj.total_credit)}</td>
                    </tr>
                  </tbody>
                </table>
              </Panel>
            )}

            {/* Income Audit */}
            <Panel label="Income Audit">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Revenue Center</th>
                    <th className="text-left py-1.5 px-3">Department</th>
                    <th className="text-right py-1.5 px-3">Reported</th>
                    <th className="text-right py-1.5 px-3">Settled</th>
                    <th className="text-right py-1.5 px-3">Over/(Short)</th>
                    <th className="text-right py-1.5 px-3">Open Reconciling</th>
                    <th className="text-center py-1.5 pl-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.audits.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-500 dark:text-slate-400">
                        No outlets audited for this business date.
                      </td>
                    </tr>
                  )}
                  {data.audits.map((r, i) => {
                    const pillTone =
                      r.status === "exception" ? "exception" : r.status === "open" ? "open" : "success";
                    return (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 pr-3 text-slate-800 dark:text-slate-200">{r.revenue_center}</td>
                        <td className="py-1.5 px-3 text-slate-600 dark:text-slate-300">{r.department}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs">{money(r.reported)}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs">{money(r.settled)}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs">{money(r.over_short)}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500 dark:text-slate-400">
                          {money(r.open_reconciling)}
                        </td>
                        <td className="py-1.5 pl-3 text-center">
                          <StatusPill tone={pillTone} className="capitalize">
                            {r.status}
                          </StatusPill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Panel>

            {/* Exceptions */}
            <Panel label="Exceptions">
              {data.exceptions.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No exceptions — the night auditor is clear to post.
                </div>
              ) : (
                <ul>
                  {data.exceptions.map((ex, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 py-2.5 border-t border-slate-100 dark:border-slate-800 first:border-t-0"
                    >
                      <SeverityIcon severity={ex.severity} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {ex.title}
                          {ex.revenue_center && (
                            <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                              {ex.revenue_center}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">{ex.detail}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}
